import { FC } from 'react';
import { useQuery } from 'react-query';
import {
  getOrgPolicyV1,
  getPolicyV1ById,
  patchPolicyV1,
} from '@/lib/services/policy-v1.service';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import useQueryHelper from '@/hooks/use-query-helper';
import useFormHandle from '@/hooks/use-form-handle.hook';
import FormButtons from '@/components/form/form-buttons';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { FieldError } from '@/lib/services';
import ClassifierControlsV1 from '@/components/form/policy-v1/classifier-controls-v1';
import { Stack } from '@mui/material';
import { createUseStyles } from 'react-jss';

const fields = ['intents', 'classifierControls', 'regexPatterns'];
const formModel = new PolicyModelV1();

const useStyles = createUseStyles({
  classificationForm: {
    height: '100%',
    minHeight: 'calc(100vh - 120px)', // Put in place to have the sticky footer work when too little content. Account for header (~64px) + tabs (~56px)
    display: 'flex',
    flexDirection: 'column',
  },
  contentWrapper: {
    flexGrow: 1,
    flexDirection: 'column',
  },
});

interface Props {
  policyId: string;
}

const IntentsForm: FC<Props> = ({ policyId }) => {
  const { toast, errorToast } = useToast();
  const { canChangePolicy } = useFeature();
  const styles = useStyles();

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const { patched, fieldErrors, error } = await patchPolicyV1(
        values.id,
        values.propSlice(fields)
      );
      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The intent was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The intent was not updated');
    },
  });

  const query = useQuery([QueryKey.PolicyCompView, policyId], async () => {
    const [policy, orgPolicy] = await Promise.all([
      getPolicyV1ById(policyId),
      getOrgPolicyV1(),
    ]);
    if (!policy || !orgPolicy) {
      return [policy || null, orgPolicy || undefined];
    }

    policy.applyOrgPolicy(orgPolicy);

    return [policy, orgPolicy];
  });

  const { showLoader } = useQueryHelper(query);
  const { data } = query;
  const [policy, orgPolicy] = data || [null, undefined];

  if (showLoader) {
    return;
  }

  return (
    <Form formHandle={formHandle} query={query} className={styles.classificationForm}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />
      <Stack className={styles.contentWrapper}>
        <ClassifierControlsV1
          formHandle={formHandle}
          readonly={!canChangePolicy}
          policy={policy!}
          orgPolicy={orgPolicy || undefined}
        />
      </Stack>
      <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
    </Form>
  );
};

export default IntentsForm;
